import React, { useState } from 'react';

import { Tab } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';

import { useMountEffect } from 'shared/hooks';
import useHl7PatientEdit from 'features/admin/hooks/useHl7PatientEdit';
import useGoBack from 'navigation/hooks/useGoBack';
import BackButton from 'navigation/components/BackButton/BackButton';
import Hl7PatientForm from 'features/admin/components/Hl7PatientForm';
import { mapApi2Form } from 'features/admin/components/Hl7PatientForm/utils';
import PageTitle from 'shared/components/layout/PageTitle/PageTitle';
import EditButton from 'shared/components/buttons/EditButton';
import ActionButton from 'shared/components/buttons/ActionButton/ActionButton';

import {
  Box,
  FlexBoxColumn,
  FlexBoxColumnFull,
  FlexBoxAlign,
  FlexBoxSpaceBetween,
} from 'styles/layout';

import styled from '@emotion/styled';

import Hl7OrdersListTab from './Hl7OrdersListTab';
import Hl7VisitsListTab from './Hl7VisitsListTab';

const FormButton = styled(ActionButton)`
  width: 120px;
`;

const Hl7PatientDetailsPage = () => {
  const { goBack } = useGoBack();

  const {
    hl7PatientId,
    patientData,
    isEditMode,
    enableEditMode,
    disableEditMode,
    fetchPatient,
    onPatientUpdate,
  } = useHl7PatientEdit();

  useMountEffect(fetchPatient);

  const initValues = React.useMemo(
    () => mapApi2Form(patientData),
    [patientData],
  );

  const tabs = React.useMemo(
    () => [
      {
        key: 'patientDetails',
        label: 'Patient Details',
        render: () => (
          <FlexBoxColumnFull p={2}>
            <Hl7PatientForm
              initValues={initValues}
              hideButtons
              disabled={!isEditMode}
              onSubmit={onPatientUpdate}
              onCancel={disableEditMode}
            />
          </FlexBoxColumnFull>
        ),
      },
      {
        key: 'hl7Orders',
        label: 'HL7 Orders',
        render: () => <Hl7OrdersListTab hl7PatientId={hl7PatientId} />,
      },
      {
        key: 'hl7Visits',
        label: 'HL7 Visits',
        render: () => (
          <Hl7VisitsListTab
            hl7PatientId={hl7PatientId}
            patientData={patientData}
          />
        ),
      },
    ],
    [
      initValues,
      isEditMode,
      disableEditMode,
      onPatientUpdate,
      hl7PatientId,
      patientData,
    ],
  );

  const [tab, setTab] = useState(tabs[0].key);

  return (
    <FlexBoxColumn height="100%">
      <FlexBoxSpaceBetween>
        <FlexBoxAlign>
          {!isEditMode && <BackButton onBack={goBack} />}
          <PageTitle>HL7 Patient Details</PageTitle>
        </FlexBoxAlign>
        <FlexBoxAlign>
          {!isEditMode && tab === tabs[0].key && (
            <EditButton onClick={enableEditMode} />
          )}

          {isEditMode && tab === tabs[0].key && (
            <>
              <Box ml={3.5} mr={3.5}>
                <FormButton color="secondary" form="hl7PatientForm">
                  Save
                </FormButton>
              </Box>
              <FormButton
                type="button"
                variant="outlined"
                color="secondary"
                onClick={disableEditMode}
              >
                Cancel
              </FormButton>
            </>
          )}
        </FlexBoxAlign>
      </FlexBoxSpaceBetween>

      <TabContext value={tab}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList onChange={(_, newValue) => setTab(newValue)}>
            {tabs.map(({ key, label }) => (
              <Tab key={key} label={label} value={key} />
            ))}
          </TabList>
        </Box>
        {tabs.map(({ key, render }) => (
          <TabPanel key={key} value={key} sx={{ p: 0, flex: 1 }}>
            {render()}
          </TabPanel>
        ))}
      </TabContext>
    </FlexBoxColumn>
  );
};

export default Hl7PatientDetailsPage;
